import React from "react";

const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * Reusable "Sort by First/Last name" + alphabet jump bar.
 * - sortBy: "first" | "last"
 * - onSort(next): callback
 * - available: Set of uppercase letters that have entries
 * - onJump(letter): scroll to first entry for that letter
 * - idPrefix: used only for test ids uniqueness (e.g. "fac", "afac")
 */
export default function SortAlphaBar({ sortBy, onSort, available, onJump, idPrefix = "fac" }) {
  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap" data-testid={`${idPrefix}-sort`}>
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Sort by</span>
        <button data-testid={`${idPrefix}-sort-first`} onClick={() => onSort("first")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sortBy === "first" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>First name</button>
        <button data-testid={`${idPrefix}-sort-last`} onClick={() => onSort("last")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sortBy === "last" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>Last name</button>
        <button data-testid={`${idPrefix}-sort-status`} onClick={() => onSort("status")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sortBy === "status" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>Status</button>
      </div>
      {sortBy !== "status" && sortBy !== "years" && (
        <div className="flex flex-wrap gap-1" data-testid={`${idPrefix}-alpha`}>
          {ALPHA.map((L) => {
            const has = available.has(L);
            return (
              <button key={L} data-testid={`${idPrefix}-alpha-${L}`} onClick={() => has && onJump(L)} disabled={!has} title={has ? `Jump to ${L}` : undefined}
                className={`h-7 w-7 rounded text-xs font-semibold transition-colors ${has ? "text-primary hover:bg-primary hover:text-white" : "text-muted-foreground/25 cursor-default"}`}>{L}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const STATUS_ORDER = { current: 0, retired: 1, emeritus: 2, deceased: 3 };

// Helpers shared by faculty listings.
export const lastNameOf = (name) => {
  const p = (name || "").trim().split(/\s+/).filter(Boolean);
  // Drop common honorifics so "Dr. Louise Cowan" sorts under C, not D.
  const honorifics = new Set(["dr", "dr.", "sr", "sr.", "fr", "fr.", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "prof", "prof.", "coach", "rev", "rev."]);
  const clean = p.filter((w, i) => !(i === 0 && honorifics.has(w.toLowerCase())));
  const arr = clean.length ? clean : p;
  return arr.length > 1 ? arr[arr.length - 1] : (arr[0] || "");
};

export const firstNameOf = (name) => {
  const p = (name || "").trim().split(/\s+/).filter(Boolean);
  const honorifics = new Set(["dr", "dr.", "sr", "sr.", "fr", "fr.", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "prof", "prof.", "coach", "rev", "rev."]);
  const clean = p.filter((w, i) => !(i === 0 && honorifics.has(w.toLowerCase())));
  return (clean.length ? clean : p)[0] || "";
};

export const sortFaculty = (list, sortBy, dir = "asc") => {
  const sign = dir === "desc" ? -1 : 1;
  return [...list].sort((a, b) => {
    let r;
    if (sortBy === "status") {
      const ra = STATUS_ORDER[a.status] ?? 9;
      const rb = STATUS_ORDER[b.status] ?? 9;
      r = ra !== rb ? ra - rb : firstNameOf(a.name).toLowerCase().localeCompare(firstNameOf(b.name).toLowerCase());
    } else if (sortBy === "years") {
      const ya = a.year_start == null ? Infinity : a.year_start;
      const yb = b.year_start == null ? Infinity : b.year_start;
      r = ya !== yb ? ya - yb : lastNameOf(a.name).toLowerCase().localeCompare(lastNameOf(b.name).toLowerCase());
    } else {
      const ka = (sortBy === "last" ? lastNameOf(a.name) : firstNameOf(a.name)).toLowerCase();
      const kb = (sortBy === "last" ? lastNameOf(b.name) : firstNameOf(b.name)).toLowerCase();
      r = ka.localeCompare(kb);
    }
    return r * sign;
  });
};

export const letterOfFaculty = (name, sortBy) =>
  (((sortBy === "last" ? lastNameOf(name) : firstNameOf(name)).trim()[0]) || "").toUpperCase();
